import { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Sparkles, 
  Loader2, 
  RefreshCw,
  AlertTriangle,
  Bot,
  Shield,
  CheckCircle,
  Target,
  BarChart3,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const AIProcessOptimizer = ({ mapId, mapName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/process-maps/${mapId}/ai-optimize`);
      setAnalysis(response.data);
      toast.success('AI analysis complete');
    } catch (error) {
      console.error('AI analysis failed:', error);
      toast.error('Failed to analyze process map');
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    
    // Simple markdown rendering
    const lines = text.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];
    
    lines.forEach((line, i) => {
      // Headers
      if (line.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`list-${i}`} className="list-disc pl-6 mb-4 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const headerText = line.replace('## ', '');
        const icon = getHeaderIcon(headerText);
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-slate-900 mt-6 mb-3 flex items-center gap-2">
            {icon}
            {headerText}
          </h3>
        );
      }
      // Bold text sections
      else if (line.startsWith('**') && line.endsWith('**')) {
        if (inList) {
          elements.push(<ul key={`list-${i}`} className="list-disc pl-6 mb-4 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <p key={i} className="font-medium text-slate-800 mt-3 mb-1">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      }
      // List items
      else if (line.startsWith('- ')) {
        inList = true;
        const content = line.replace('- ', '');
        listItems.push(
          <li key={i} className="text-slate-600 text-sm">
            {formatInlineMarkdown(content)}
          </li>
        );
      }
      // Regular paragraphs
      else if (line.trim()) {
        if (inList) {
          elements.push(<ul key={`list-${i}`} className="list-disc pl-6 mb-4 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <p key={i} className="text-slate-600 text-sm mb-2">
            {formatInlineMarkdown(line)}
          </p>
        );
      }
    });
    
    // Flush remaining list items
    if (inList && listItems.length > 0) {
      elements.push(<ul key="final-list" className="list-disc pl-6 mb-4 space-y-1">{listItems}</ul>);
    }
    
    return elements;
  };

  const formatInlineMarkdown = (text) => {
    // Handle bold
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-medium text-slate-800">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const getHeaderIcon = (text) => {
    if (text.includes('Bottleneck')) return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (text.includes('Automation')) return <Bot className="w-5 h-5 text-blue-500" />;
    if (text.includes('Compliance') || text.includes('Risk')) return <Shield className="w-5 h-5 text-amber-500" />;
    if (text.includes('Best Practice')) return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    if (text.includes('Quick Wins')) return <Target className="w-5 h-5 text-purple-500" />;
    if (text.includes('Metrics')) return <BarChart3 className="w-5 h-5 text-cyan-500" />;
    return <Sparkles className="w-5 h-5 text-[#26B8A7]" />;
  };

  return (
    <Card className="border-2 border-[#26B8A7] bg-gradient-to-br from-[#26B8A7]/5 to-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#26B8A7]" />
            <CardTitle className="text-lg">AI Process Optimizer</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <CardDescription>
          AI-powered analysis for bottlenecks, automation opportunities, and compliance gaps
        </CardDescription>
      </CardHeader>
      
      {expanded && (
        <CardContent>
          {!analysis ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-[#26B8A7]/30 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                Analyze "{mapName}" for optimization opportunities
              </p>
              <Button 
                onClick={runAnalysis}
                disabled={loading}
                className="bg-gradient-to-r from-[#235894] to-[#26B8A7] hover:opacity-90"
                data-testid="run-ai-analysis-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing Process...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Run AI Analysis
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-400 mt-3">
                Analysis takes 15-30 seconds
              </p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="outline" className="text-xs">
                  Generated {new Date(analysis.generated_at).toLocaleString('en-GB')}
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={runAnalysis}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="ml-1">Re-analyze</span>
                </Button>
              </div>
              
              <div className="prose prose-sm max-w-none border-t pt-4">
                {renderMarkdown(analysis.analysis)}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AIProcessOptimizer;
